import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getColor,
  getColorMenu,
  getColorText,
  getColorThemeAsync,
  getDarkMode,
  getThemeColor,
  setDarkMode,
} from 'store/settings';
import { getSelectedCentre } from 'store/master';
import { THEME_MODE } from 'helpers/storage';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isDarkMode = useSelector(getDarkMode);
  const themeColor = useSelector(getThemeColor);
  const buttonColor = useSelector(getColor);
  const buttonTextColor = useSelector(getColorText);
  const menuTextColor = useSelector(getColorMenu);
  const selectedCentre = useSelector(getSelectedCentre);
  const darkModeInitialized = useRef(false);

  // Synchronize with localStorage preference on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedTheme = THEME_MODE.get();
      // const storedTheme = localStorage.getItem('lmsThemeMode');
      if (storedTheme === 'dark' || storedTheme === 'light') {
        dispatch(setDarkMode(storedTheme === 'dark'));
      }
    }
  }, [dispatch]);

  // Fetch color theme configuration on mount and selected centre changes
  useEffect(() => {
    dispatch(getColorThemeAsync());
  }, [dispatch, selectedCentre]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!darkModeInitialized.current) {
      darkModeInitialized.current = true;
      document.documentElement.classList.toggle('dark', isDarkMode);
      document.body?.classList.toggle('dark', isDarkMode);
      return;
    }

    document.documentElement.classList.toggle('dark', isDarkMode);
    document.body?.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const themeVariables = {
    '--theme-bg-clr': themeColor,
    '--theme-clr': themeColor,
    '--btn-bg-clr': buttonColor,
    '--text-clr': buttonTextColor,
    '--menu-bg-clr': menuTextColor,
    'color-scheme': isDarkMode ? 'dark' : 'light',
  };

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;

    Object.entries(themeVariables).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(key, value);
      } else {
        root.style.removeProperty(key);
      }
    });
  }, [themeColor, buttonColor, buttonTextColor, menuTextColor, isDarkMode]);

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        themeColor,
        buttonColor,
        buttonTextColor,
        menuTextColor,
        themeVariables,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
